import React, { useMemo } from 'react'
import { graphql, useFragment } from 'react-relay'

import { XStack, YStack } from 'tamagui'

import { Paragraph, Strong } from '@newComponents/Text'
import { Stepper } from '@newComponents/Form/FormStepper'

import { TierSelectionItem_tier$key } from './__generated__/TierSelectionItem_tier.graphql'

export const TierSelectionItem = ({
  tier,
  quantity,
  onQuantityChange,
}: {
  tier: TierSelectionItem_tier$key
  quantity: number
  onQuantityChange: (qty: number) => void
}) => {
  const data = useFragment(
    graphql`
      fragment TierSelectionItem_tier on EventTicketingTier {
        name
        description
        price
        currency
        includeTax
        taxName
        taxPercentage
        status
        availableFrom
        availableUntil
        ticketLimit
        minPerOrder
        maxPerOrder
      }
    `,
    tier
  )

  const isAvailable = useMemo(() => {
    const now = new Date()
    if (data.status !== 'active') return false
    if (data.availableFrom && new Date(data.availableFrom as string) > now)
      return false
    if (data.availableUntil && new Date(data.availableUntil as string) < now)
      return false
    return true
  }, [data.status, data.availableFrom, data.availableUntil])

  const unavailableReason = useMemo(() => {
    if (data.status !== 'active') return 'Sales paused'
    const now = new Date()
    if (data.availableFrom && new Date(data.availableFrom as string) > now)
      return 'Not yet available'
    if (data.availableUntil && new Date(data.availableUntil as string) < now)
      return 'Sales ended'
    return undefined
  }, [data.status, data.availableFrom, data.availableUntil])

  const formattedPrice = useMemo(() => {
    if (data.price === 0) return 'Free'

    const price = data.price / 100
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: data.currency,
        maximumFractionDigits: price % 1 === 0 ? 0 : undefined,
      }).format(price)
    } catch {
      return `${price} ${data.currency}`
    }
  }, [data.price, data.currency])

  const minQuantity = data.minPerOrder ?? 1
  const maxQuantity = data.maxPerOrder ?? data.ticketLimit ?? 100

  const handleDecrement = () => {
    if (quantity <= minQuantity) {
      onQuantityChange(0)
    } else {
      onQuantityChange(quantity - 1)
    }
  }

  const handleIncrement = () => {
    if (quantity === 0) {
      onQuantityChange(minQuantity)
    } else {
      onQuantityChange(Math.min(maxQuantity, quantity + 1))
    }
  }

  return (
    <XStack gap='$4'>
      <YStack gap='$1' flex={1}>
        <Strong size='$5'>{data.name}</Strong>

        {data.description ? (
          <Paragraph size='$4'>{data.description}</Paragraph>
        ) : null}

        <XStack gap='$2' alignItems='center'>
          <Strong
            size='$6'
            fontWeight='$7'
            style={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {formattedPrice}
          </Strong>

          {data.taxName && data.taxPercentage ? (
            <Paragraph size='$3' color='$colorSubtle'>
              {data.includeTax ? 'incl.' : 'excl.'} {data.taxName} @{' '}
              {(data.taxPercentage ?? 0) * 100}%
            </Paragraph>
          ) : null}
        </XStack>

        {minQuantity > 1 && (
          <Paragraph size='$3' color='$colorSubtle'>
            Minimum {minQuantity} per order
          </Paragraph>
        )}
      </YStack>

      <YStack alignItems='center' justifyContent='center'>
        {isAvailable ? (
          <Stepper
            value={quantity}
            onChange={onQuantityChange}
            onDecrement={handleDecrement}
            onIncrement={handleIncrement}
            decrementDisabled={quantity <= 0}
            incrementDisabled={quantity >= maxQuantity}
          />
        ) : (
          <Paragraph size='$4' color='$colorMuted'>
            {unavailableReason}
          </Paragraph>
        )}
      </YStack>
    </XStack>
  )
}
